import { ErrorService } from '@/shared/service/errorService'
import { create } from '@/shared/utils/zustandVue'
import { cloneDeep } from '@/utils'

import { t } from '../../i18n/i18n'
import {
  type Festival,
  FestivalService,
} from '../../shared/service/festival.service'
import { SnackbarService } from '../../shared/snackbar/snackbar.service'

class FestivalState {
  festival: Festival | undefined
}

type FestivalStore = FestivalState & {
  setFestival: (festival: Festival) => void
  loadFestival: () => Promise<void>
  updateFestival: (festival: Festival) => Promise<void>
}

export const useFestivalStore = create<FestivalStore>((set, get) => ({
  ...new FestivalState(),
  setFestival: (festival) => {
    set({ festival })
  },
  async loadFestival() {
    try {
      const festival = await FestivalService.getFestival()
      set({ festival })
    } catch (e) {
      ErrorService.handleError(e)
    }
  },
  async updateFestival(festival) {
    try {
      festival._id = get().festival?._id
      const updatedFestival = await FestivalService.updateFestival(festival)
      SnackbarService.info(t('FESTIVAL.SNACK_BAR.UPDATE'))
      set({ festival: updatedFestival })
    } catch (e) {
      ErrorService.handleError(e)
    }
  },
}))

export function computeFestival(state: FestivalState): Festival | undefined {
  if (!state.festival) {
    return undefined
  }
  const festival = cloneDeep(state.festival)
  festival.name = state.festival.name ?? ''
  festival.links = state.festival.links ?? {}
  festival.startDate = state.festival.startDate ?? ''
  festival.endDate = state.festival.endDate ?? ''
  festival.contact = state.festival.contact ?? {
    tel: '',
    mail: '',
  }
  festival.locationName = state.festival.locationName ?? ''
  festival.alwaysShowEventHour = Boolean(state.festival.alwaysShowEventHour)
  // the backend sends null when no export url was ever saved
  if (!Array.isArray(festival.gcExportUrls)) {
    festival.gcExportUrls = [
      { type: 'SCENE', url: '' },
      { type: 'ARTIST', url: '' },
      { type: 'EVENT', url: '' },
    ]
  }
  festival.orderEventsByEndDate = state.festival.orderEventsByEndDate
  festival.eventsHideEndDate = Boolean(state.festival.eventsHideEndDate)
  festival.eventsHideLocation = Boolean(state.festival.eventsHideLocation)
  festival.saferApiKey = state.festival.saferApiKey
  festival.defaultEventImage = state.festival.defaultEventImage
  return festival
}
